<script lang="ts" setup>
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import emblaCarouselVue from 'embla-carousel-vue'
type Specie = {
  title: string
  latinName: string
  family: string
  text: string
  color: string
  status: string
  media: Record<string, unknown>
}

type Props = {
  title?: string
  items: Specie[]
}

const _props = defineProps<{ data: Props; index: number }>()
const emit = defineEmits(['active-index'])

const [emblaRef, emblaApi] = emblaCarouselVue({ align: 'start' }, [WheelGesturesPlugin()])

const activeIndex = ref(0)

watch(activeIndex, () => {
  emit('active-index', activeIndex.value)
})

const state = ref({
  canScrollPrev: false,
  canScrollNext: true,
})

onMounted(() => {
  if (emblaApi.value) {
    const togglePrevNextBtnsState = () => {
      state.value.canScrollPrev = activeIndex.value != 0
      state.value.canScrollNext = activeIndex.value <= _props.data.items.length - 1
      activeIndex.value = emblaApi.value?.selectedScrollSnap() || 0 // Update activeIndex
    }

    emblaApi.value
      .on('select', togglePrevNextBtnsState)
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState)

    // Scroll to initial index on mount
    emblaApi.value.scrollTo(_props.index, 0)
    activeIndex.value = _props.index
  }
})

// Watch for prop index changes
watch(
  () => _props.index,
  (newIndex) => {
    if (emblaApi.value) {
      emblaApi.value.scrollTo(newIndex, 0)
      activeIndex.value = newIndex
    }
  },
)

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

const handleNavigate = (direction) => {
  activeIndex.value = direction == 'next' ? activeIndex.value + 1 : activeIndex.value - 1

  if (activeIndex.value > _props.data.items.length - 1) {
    activeIndex.value = _props.data.items.length - 1
  }

  emblaApi.value?.scrollTo(activeIndex.value)
}
</script>

<template>
  <div class="">
    <div class="px-bleed mt-7.5 mb-5 flex">
      <div class="flex w-full items-center justify-between">
        <div>
          <h2 v-if="data.title" class="text-sm font-bold uppercase lg:text-(--white)">
            {{ data.title }}
          </h2>
        </div>

        <div v-if="(isMobile && data.items?.length > 1) || data.items?.length > 4" class="shrink-0">
          <elementsArrowButton
            class="cursor-pointer"
            :class="!state.canScrollPrev ? 'opacity-30' : ''"
            :disabled="!state.canScrollPrev"
            aria-label="Previous slide"
            variant="back"
            @click="handleNavigate('prev')"
          />

          <elementsArrowButton
            class="cursor-pointer"
            :class="!state.canScrollNext ? 'opacity-30' : ''"
            :disabled="!state.canScrollNext"
            aria-label="Next slide"
            @click="handleNavigate('next')"
          />
        </div>
      </div>
    </div>

    <div ref="emblaRef" class="embla overflow-hidden pb-5 lg:z-[2]">
      <div class="embla__container relative z-[2] w-full">
        <button
          v-for="(item, idx) in data.items"
          :key="idx"
          class="embla__slide cursor-pointer text-left opacity-40"
          :class="{ 'opacity-100': activeIndex === idx }"
          @click="
            () => {
              activeIndex = idx
              emblaApi?.scrollTo(idx, 0)
            }
          "
        >
          <span class="font-display inline text-xl whitespace-nowrap lg:text-2xl">{{
            item.title
          }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.embla {
  --slide-spacing: 20px;
  --slide-size: fit-content;
  @media (min-width: 900px) {
    --slide-spacing: 50px;
  }
}
.embla__viewport {
  overflow: hidden;
}
.embla__container {
  display: flex;
  touch-action: pan-y pinch-zoom;
}

.embla__slide {
  transform: translate3d(0, 0, 0);
  flex: 0 0 var(--slide-size);
  min-width: 0;
  padding-left: var(--grid-bleed);

  &:last-of-type {
    padding-right: var(--grid-bleed);
  }
}
</style>
